/**
 * Cache stale-while-revalidate nhẹ, phạm vi một PHIÊN ĐĂNG NHẬP.
 *
 * Dùng cho các khối số liệu nằm trên đường tới hạn của lần vẽ đầu (menu sidebar, mini
 * dashboard): vẽ ngay bản cache rồi gọi API làm mới ở nền, thay vì để người dùng ngồi
 * xem skeleton mỗi lần F5.
 *
 * KHÔNG dùng cho dữ liệu phải luôn chính xác tại thời điểm xem (số dư, trạng thái thanh
 * toán của một khoản cụ thể) — bản stale ở đó là gây hiểu sai, không phải tối ưu.
 *
 * File này CỐ Ý không import gì trong project (tự đọc cookie thay vì dùng
 * utils/config.js) để mọi tầng — redux slice, hook, interceptor — đều import được mà
 * không tạo vòng phụ thuộc.
 */

const PREFIX = "onepay.swr.";

// maxAge KHÔNG phải để giới hạn đường chạy bình thường — mọi call site đều revalidate
// ngay khi mount nên bản stale chỉ sống khoảng 1 request. Nó là hàng rào cho đường
// THẤT BẠI: khi API lỗi/timeout, call site giữ nguyên bản stale đang vẽ, và maxAge là
// thứ duy nhất quyết định bản stale đó được phép cũ tới đâu.
//
// Dữ liệu cấu hình (menu): cũ vài ngày vẫn gần như đúng, sai thì cùng lắm bấm vào gặp 403.
export const SWR_MAX_AGE_LONG = 7 * 24 * 60 * 60 * 1000;
// Số liệu tiền/tiến độ: hết 5 phút thì thà trả null cho card hiện 0 và chờ API, còn hơn
// vẽ một con số cũ mà người dùng không có cách nào biết là cũ rồi ra quyết định theo nó.
export const SWR_MAX_AGE_SHORT = 5 * 60 * 1000;

const DEFAULT_MAX_AGE = SWR_MAX_AGE_LONG;

const readCookie = (name) => {
  const target = `${name}=`;
  const parts = document.cookie.split(";");
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trimStart();
    if (part.indexOf(target) === 0) return part.substring(target.length);
  }
  return null;
};

/**
 * Định danh phiên = id của Sanctum token ("317|xxxxx" -> "317") + vai trò đang làm việc.
 * Chỉ lấy phần id, KHÔNG đưa phần secret của token vào localStorage.
 * Đổi account hoặc đổi vai trò là key lệch -> cache tự vô hiệu, không đọc lại số liệu
 * của người trước.
 */
export const getSwrSessionKey = () => {
  const token = readCookie("access_token");
  if (!token) return null;
  const tokenId = token.split(/\||%7C/i)[0];
  return `${tokenId}:${readCookie("type_user") || ""}`;
};

/**
 * @returns payload đã cache nếu còn hợp lệ với phiên hiện tại, ngược lại null.
 */
export const readSwrCache = (key, { maxAge = DEFAULT_MAX_AGE } = {}) => {
  const sessionKey = getSwrSessionKey();
  if (!sessionKey) return null;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached?.sessionKey !== sessionKey) return null;
    if (Date.now() - cached.ts > maxAge) return null;
    return cached.payload ?? null;
  } catch {
    // JSON hỏng hoặc không đọc được storage: coi như chưa có cache.
    return null;
  }
};

export const writeSwrCache = (key, payload) => {
  const sessionKey = getSwrSessionKey();
  if (!sessionKey || payload === undefined || payload === null) return;
  try {
    localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ sessionKey, ts: Date.now(), payload })
    );
  } catch {
    // localStorage đầy hoặc private mode: bỏ qua, SWR chỉ là tối ưu.
  }
};

/**
 * Dọn toàn bộ cache SWR. Gọi khi đăng xuất / gặp 401 để không bỏ lại số liệu của phiên
 * cũ trong localStorage.
 */
export const clearSwrCache = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Không truy cập được storage: không có gì phải dọn.
  }
};
